<template>
    <Teleport to="body">
        <Transition name="sp-coming-soon">
            <div
                v-if="modelValue"
                class="sp-coming-soon-backdrop"
                :style="{ zIndex: layerZ }"
                role="dialog"
                aria-modal="true"
                :aria-labelledby="titleId"
                :aria-describedby="copyId"
                @click.self="close"
            >
                <div class="sp-coming-soon-card" :class="{ 'is-feedback': allowFeedback }">
                    <div class="sp-coming-soon-card__glow" aria-hidden="true"></div>
                    <button
                        type="button"
                        class="sp-coming-soon-card__close"
                        aria-label="Close"
                        @click="close"
                    >
                        <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <div class="sp-coming-soon-card__icon" aria-hidden="true">
                        <slot name="icon">
                            <svg class="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.6">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </slot>
                    </div>
                    <p :id="titleId.replace('-title', '-eyebrow')" class="sp-coming-soon-card__eyebrow">{{ eyebrow }}</p>
                    <h2 :id="titleId" class="sp-coming-soon-card__title">{{ title }}</h2>
                    <p :id="copyId" class="sp-coming-soon-card__copy">{{ copy }}</p>
                    <StatePortalComingSoonInterest
                        :key="`modal-interest-${featureKey || uid}`"
                        :feature-key="featureKey"
                        :feature-label="resolvedFeatureLabel"
                        :surface="surface"
                        :allow-feedback="allowFeedback"
                    />
                    <button
                        type="button"
                        class="sp-coming-soon-card__cta"
                        @click="close"
                    >
                        {{ ctaLabel }}
                    </button>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script>
import StatePortalComingSoonInterest from '@/components/Instructor/StatePortal/StatePortalComingSoonInterest.vue';

let comingSoonModalSeq = 0;

export default {
    name: 'StatePortalComingSoonModal',
    components: {
        StatePortalComingSoonInterest,
    },
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        eyebrow: {
            type: String,
            default: 'State Portal',
        },
        title: {
            type: String,
            default: 'Coming soon',
        },
        copy: {
            type: String,
            default: 'This feature isn’t available yet. We’re still finishing it up.',
        },
        ctaLabel: {
            type: String,
            default: 'Got it',
        },
        featureKey: {
            type: String,
            default: '',
        },
        featureLabel: {
            type: String,
            default: '',
        },
        surface: {
            type: String,
            default: 'state-portal-coming-soon',
        },
        layerZ: {
            type: Number,
            default: 200100,
        },
        allowFeedback: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['update:modelValue'],
    data() {
        comingSoonModalSeq += 1;
        return {
            uid: comingSoonModalSeq,
        };
    },
    computed: {
        titleId() {
            return `sp-coming-soon-title-${this.uid}`;
        },
        copyId() {
            return `sp-coming-soon-copy-${this.uid}`;
        },
        resolvedFeatureLabel() {
            return String(this.featureLabel || this.eyebrow || this.title || '').trim();
        },
    },
    methods: {
        close() {
            this.$emit('update:modelValue', false);
        },
    },
};
</script>

<style scoped>
.sp-coming-soon-backdrop {
    position: fixed;
    inset: 0;
    z-index: 200100;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.25rem;
    background:
        radial-gradient(ellipse 80% 60% at 50% 40%, rgba(13, 148, 136, 0.18), transparent 60%),
        rgba(15, 23, 42, 0.55);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
}

.sp-coming-soon-card {
    position: relative;
    width: min(100%, 24rem);
    overflow: hidden;
    border-radius: 1.25rem;
    border: 1px solid rgba(226, 232, 240, 0.9);
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.7) inset,
        0 24px 48px -20px rgba(15, 23, 42, 0.45),
        0 10px 20px -12px rgba(13, 148, 136, 0.25);
    padding: 1.75rem 1.5rem 1.5rem;
    text-align: center;
}

.sp-coming-soon-card.is-feedback {
    width: min(100%, 30rem);
    overflow: visible;
}

.sp-coming-soon-card__glow {
    pointer-events: none;
    position: absolute;
    inset: -40% -20% auto;
    height: 10rem;
    background: radial-gradient(circle at 50% 0%, rgba(45, 212, 191, 0.28), transparent 70%);
}

.sp-coming-soon-card__close {
    position: absolute;
    top: 0.85rem;
    right: 0.85rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 999px;
    border: 0;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    transition: background-color 150ms ease, color 150ms ease;
}

.sp-coming-soon-card__close:hover {
    background: #f1f5f9;
    color: #475569;
}

.sp-coming-soon-card__icon {
    position: relative;
    margin: 0.25rem auto 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 1rem;
    color: #0f766e;
    background: linear-gradient(145deg, rgba(204, 251, 241, 0.95), rgba(240, 253, 250, 0.7));
    border: 1px solid rgba(45, 212, 191, 0.35);
    box-shadow: 0 8px 18px -10px rgba(13, 148, 136, 0.55);
}

.sp-coming-soon-card__eyebrow {
    position: relative;
    margin-top: 1.1rem;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #0d9488;
}

.sp-coming-soon-card__title {
    position: relative;
    margin-top: 0.35rem;
    font-size: 1.5rem;
    line-height: 1.2;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: #0f172a;
}

.sp-coming-soon-card__copy {
    position: relative;
    margin: 0.75rem auto 0;
    max-width: 22rem;
    font-size: 0.875rem;
    line-height: 1.5;
    color: #64748b;
}

.sp-coming-soon-card__cta {
    position: relative;
    margin-top: 1.35rem;
    width: 100%;
    border: 0;
    border-radius: 0.75rem;
    padding: 0.7rem 1rem;
    font-size: 0.875rem;
    font-weight: 700;
    color: #fff;
    cursor: pointer;
    background: linear-gradient(180deg, #14b8a6 0%, #0d9488 100%);
    box-shadow: 0 10px 20px -12px rgba(13, 148, 136, 0.8);
    transition: filter 150ms ease, transform 150ms ease;
}

.sp-coming-soon-card__cta:hover {
    filter: brightness(1.05);
}

.sp-coming-soon-card__cta:active {
    transform: translateY(1px);
}

.sp-coming-soon-enter-active,
.sp-coming-soon-leave-active {
    transition: opacity 180ms ease;
}

.sp-coming-soon-enter-active .sp-coming-soon-card,
.sp-coming-soon-leave-active .sp-coming-soon-card {
    transition: transform 200ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease;
}

.sp-coming-soon-enter-from,
.sp-coming-soon-leave-to {
    opacity: 0;
}

.sp-coming-soon-enter-from .sp-coming-soon-card,
.sp-coming-soon-leave-to .sp-coming-soon-card {
    opacity: 0;
    transform: translateY(10px) scale(0.97);
}

@media (prefers-reduced-motion: reduce) {
    .sp-coming-soon-enter-active,
    .sp-coming-soon-leave-active,
    .sp-coming-soon-enter-active .sp-coming-soon-card,
    .sp-coming-soon-leave-active .sp-coming-soon-card {
        transition: none;
    }
}
</style>
